import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PublicLayout, PageContainer, PageHeader } from "@/components/PublicLayout";
import { SeoContent } from "@/components/SeoContent";
import { useSeo } from "@/lib/useSeo";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";

const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020];

function ResultsTable({ rows, testId }) {
  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)]" data-testid={testId}>
      <table className="w-full">
        <thead>
          <tr className="bg-brand text-white">
            <th className="py-3.5 px-3 text-center font-heading text-xs sm:text-sm font-bold uppercase tracking-wider w-16">Sl.</th>
            <th className="py-3.5 px-3 text-left font-heading text-xs sm:text-sm font-bold uppercase tracking-wider">Date</th>
            <th className="py-3.5 px-3 text-center font-heading text-xs sm:text-sm font-bold uppercase tracking-wider border-l border-white/20">F/R</th>
            <th className="py-3.5 px-3 text-center font-heading text-xs sm:text-sm font-bold uppercase tracking-wider border-l border-white/20">S/R</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`prev-row-${i}`}>
              <td className="py-3.5 px-3 text-center text-slate-400 font-semibold text-sm">{i + 1}</td>
              <td className="py-3.5 px-3 text-left font-semibold text-slate-800 text-sm">{r.date}</td>
              <td className="py-3.5 px-3 text-center font-heading font-extrabold text-lg text-brand tabular-nums border-l border-slate-100">{r.fr || "-"}</td>
              <td className="py-3.5 px-3 text-center font-heading font-extrabold text-lg text-slate-800 tabular-nums border-l border-slate-100">{r.sr || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PreviousResult() {
  useSeo({
    title: "Guwahati Morning Teer Previous Result | Old Results Archive 2020-2026",
    description: "Guwahati Morning Teer previous results archive from 2020 to 2026 - all First Round and Second Round results, including Sunday Morning Teer, by date.",
    path: "/previous-result",
  });
  const [year, setYear] = useState(2026);
  const q = useQuery({
    queryKey: ["results", year],
    queryFn: async () => (await api.get(`/results?year=${year}`)).data,
  });
  const rows = q.data || [];

  return (
    <PublicLayout>
      <PageContainer className="py-10">
        <PageHeader eyebrow="Archive 2020 - 2026" title="Previous Result" subtitle="All published Morning Teer results by date. Choose a year to browse the full archive." />

        {/* Year selector */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="year-selector">
          {YEARS.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              data-testid={`year-btn-${y}`}
              className={`px-4 h-11 rounded-xl font-bold text-sm transition-colors ${
                year === y ? "bg-brand text-white shadow-md" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {y}
            </button>
          ))}
        </div>

        {q.isLoading ? (
          <LoadingState />
        ) : q.isError ? (
          <ErrorState onRetry={q.refetch} />
        ) : rows.length === 0 ? (
          <EmptyState title={`No results for ${year}`} subtitle="Try another year." />
        ) : (
          <>
            <p className="text-sm font-semibold text-slate-500 mb-3" data-testid="year-count">
              {rows.length} results in {year}
            </p>
            <ResultsTable rows={rows} testId="previous-results-table" />
          </>
        )}
        <SeoContent page="previous" />
      </PageContainer>
    </PublicLayout>
  );
}
